import type { Theme } from '@src/app/themes';
import { Button, IconButton } from '@src/shared/ui/Button';
import { useTranslation } from 'react-i18next';
import {
  CircleCheckIcon,
  CircleCloseIcon,
  CloseIcon,
  SquareInfoIcon,
} from '@src/shared/ui/icons';
import { Box, Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import type { ToastAction, ToastSeverity } from './types';

const icons = {
  success: CircleCheckIcon,
  warning: SquareInfoIcon,
  error: CircleCloseIcon,
};
const getTostColor = (theme: Theme): Record<string, string> => ({
  success: theme.palette.green[600],
  warning: theme.palette.orange[600],
  error: theme.palette.red[600],
});

const StyleSheet = {
  root:
    (severity: ToastSeverity) => (theme: Theme) => ({
      alignItems: 'center',
      bgcolor: getTostColor(theme)[severity],
      borderRadius: 1,
      color: 'common.white',
      p: 1.25,
    }),
  textBlock: {
    flex: 1,
    lineHeight: 1.34,
  },
  title: {
    fontWeight: 500,
  },
  closeButton: (severity: ToastSeverity) => (theme: Theme) => ({
    color: 'common.white',
    '&:hover': {
      color: getTostColor(theme)[severity],
      bgcolor: 'common.white',
      '& .MuiSvgIcon-root': {
     },
    },
  }),
  actionButton: {
    color: 'common.white',
    borderColor: 'common.white',
    flexShrink: 0,
    '&:hover': {
      borderColor: 'common.white',
      bgcolor: 'rgba(255, 255, 255, 0.16)',
    },
  },
};

type ToastProps = {
  title: string;
  description?: string;
  severity: ToastSeverity;
  action?: ToastAction;
  onDismiss: () => void;
};

export default function Toast({
  title,
  description,
  severity,
  action,
  onDismiss,
}: ToastProps) {
  const { t } = useTranslation();
  const Icon = icons[severity];

  return (
    <Stack
      direction="row"
      useFlexGap
      spacing={0.75}
      sx={StyleSheet.root(severity)}
    >
      <Icon color="inherit" />
      <Box sx={StyleSheet.textBlock}>
        <Typography variant="body3" component="p" sx={StyleSheet.title}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body3" component="p">
            {description}
          </Typography>
        )}
      </Box>
      {action && (
        <Button
          variant="outlined"
          size="small"
          onClick={action.onClick}
          sx={StyleSheet.actionButton}
        >
          {action.label}
        </Button>
      )}
      <IconButton
        onClick={onDismiss}
        size="small"
        sx={StyleSheet.closeButton(severity)}
        tooltipTitle={t('common.close', 'Close')}
      >
        <CloseIcon color="inherit" fontSize="small" />
      </IconButton>
    </Stack>
  );
}
